/**
 * SeeMe Tooltip
 * Portal-based floating tooltip.
 *
 * Replaces CSS ::after tooltip — escapes parent overflow clipping
 * (data-table scroll containers, sheets, modals) and avoids font/text-transform
 * inheritance bugs (e.g. Font Awesome glyph font on <i> triggers, uppercase
 * text-transform on <th> cells).
 *
 * Usage — same markup contract as the old CSS tooltip:
 *
 *   <button class="tooltip-hover" title="Label">...</button>
 *
 * Position modifiers (preferred side — JS will edge-flip if no room):
 *   .tooltip-hover--top
 *   .tooltip-hover--bottom
 *   .tooltip-hover--left
 *   .tooltip-hover--top-end    (anchor to right edge of trigger)
 *   .tooltip-hover--top-start  (anchor to left edge of trigger)
 *
 * Default side: right.
 */

(function () {
    var GAP = 8;         // px gap between trigger and tooltip
    var MARGIN = 8;      // px viewport margin for edge-flip / clamp

    var activeTrigger = null;
    var tipEl = null;
    var pointerX = null;
    var pointerY = null;

    function getSide(trigger) {
        var cl = trigger.classList;
        if (cl.contains('tooltip-hover--top-end')) return 'top-end';
        if (cl.contains('tooltip-hover--top-start')) return 'top-start';
        if (cl.contains('tooltip-hover--top')) return 'top';
        if (cl.contains('tooltip-hover--bottom')) return 'bottom';
        if (cl.contains('tooltip-hover--left')) return 'left';
        return 'right';
    }

    function position(trigger, tip, side) {
        var tRect = trigger.getBoundingClientRect();
        var tipRect = tip.getBoundingClientRect();
        var vw = window.innerWidth;
        var vh = window.innerHeight;

        var top = 0;
        var left = 0;

        // Edge-flip on same axis if no room (top<->bottom, left<->right)
        if (side === 'top' || side === 'top-end' || side === 'top-start') {
            if (tRect.top - tipRect.height - GAP < MARGIN) side = side.replace('top', 'bottom');
        } else if (side === 'bottom') {
            if (tRect.bottom + tipRect.height + GAP > vh - MARGIN) side = 'top';
        } else if (side === 'right') {
            if (tRect.right + tipRect.width + GAP > vw - MARGIN) side = 'left';
        } else if (side === 'left') {
            if (tRect.left - tipRect.width - GAP < MARGIN) side = 'right';
        }

        switch (side) {
            case 'top':
                top = tRect.top - tipRect.height - GAP;
                left = tRect.left + (tRect.width - tipRect.width) / 2;
                break;
            case 'top-end':
                top = tRect.top - tipRect.height - GAP;
                left = tRect.right - tipRect.width;
                break;
            case 'top-start':
                top = tRect.top - tipRect.height - GAP;
                left = tRect.left;
                break;
            case 'bottom':
                top = tRect.bottom + GAP;
                left = tRect.left + (tRect.width - tipRect.width) / 2;
                break;
            case 'bottom-end':
                top = tRect.bottom + GAP;
                left = tRect.right - tipRect.width;
                break;
            case 'bottom-start':
                top = tRect.bottom + GAP;
                left = tRect.left;
                break;
            case 'left':
                top = tRect.top + (tRect.height - tipRect.height) / 2;
                left = tRect.left - tipRect.width - GAP;
                break;
            case 'right':
            default:
                top = tRect.top + (tRect.height - tipRect.height) / 2;
                left = tRect.right + GAP;
                break;
        }

        // Horizontal clamp for top/bottom placements so tooltip stays in viewport
        if (side.indexOf('top') === 0 || side.indexOf('bottom') === 0) {
            var maxLeft = vw - tipRect.width - MARGIN;
            if (left < MARGIN) left = MARGIN;
            else if (left > maxLeft) left = maxLeft;
        } else {
            // Vertical clamp for left/right placements
            var maxTop = vh - tipRect.height - MARGIN;
            if (top < MARGIN) top = MARGIN;
            else if (top > maxTop) top = maxTop;
        }

        tip.style.top = top + 'px';
        tip.style.left = left + 'px';
    }

    function show(trigger) {
        if (activeTrigger === trigger) return;
        hide();

        var text = trigger.getAttribute('title');
        if (!text) return;

        // Stash + remove title to suppress native OS tooltip
        trigger.dataset.tooltipText = text;
        trigger.removeAttribute('title');

        activeTrigger = trigger;

        tipEl = document.createElement('div');
        tipEl.className = 'tooltip';
        tipEl.textContent = text;
        document.body.appendChild(tipEl);

        // Position + fade in next frame — getBoundingClientRect returns zeros
        // before layout, which breaks edge-flip math and centering.
        requestAnimationFrame(function () {
            if (!tipEl) return;
            var side = getSide(trigger);
            position(trigger, tipEl, side);
            tipEl.style.opacity = '1';
        });
    }

    function hide() {
        if (activeTrigger) {
            var saved = activeTrigger.dataset.tooltipText;
            if (saved) {
                activeTrigger.setAttribute('title', saved);
                delete activeTrigger.dataset.tooltipText;
            }
            activeTrigger = null;
        }
        if (tipEl) {
            if (tipEl.parentNode) tipEl.remove();
            tipEl = null;
        }
    }

    // Closing a menu can expose a trigger under a stationary pointer. Only
    // actual movement should open its tooltip, not the resulting mouseover.
    document.addEventListener('pointerdown', function (e) {
        pointerX = e.clientX;
        pointerY = e.clientY;
        hide();
    }, true);

    document.addEventListener('mousemove', function (e) {
        if (e.clientX === pointerX && e.clientY === pointerY) return;
        pointerX = e.clientX;
        pointerY = e.clientY;
        if (!e.target.closest) return;
        var trigger = e.target.closest('[title].tooltip-hover');
        if (!trigger) return;
        show(trigger);
    });

    document.addEventListener('mouseout', function (e) {
        if (!activeTrigger) return;
        if (!e.target.closest) return;
        // Only hide when the pointer actually leaves the active trigger
        // (relatedTarget is what it moved to)
        var leaving = e.target.closest('.tooltip-hover');
        if (leaving !== activeTrigger) return;
        var to = e.relatedTarget;
        if (to && to.nodeType === 1 && activeTrigger.contains(to)) return;
        hide();
    });

    // Hide on scroll/resize — don't reposition, just close.
    window.addEventListener('scroll', function () {
        if (activeTrigger) hide();
    }, true);

    window.addEventListener('resize', function () {
        if (activeTrigger) hide();
    });
})();
